import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import theme from '@adsk/alloy-react-theme';

import EntityPickerModalListContent from './EntityPickerModalListContent';
import EntityPickerModalNoData from './EntityPickerModalNoData';
import { entityShape, pluginShape } from '../helpers/propTypes';

const EntityPickerModalContent = ({
  onLoadMore,
  selectedIds,
  disabledIds,
  multiple,
  entities,
  addEntitiesToCache,
  onUpdate,
  loading,
  error,
  onTryAgain,
  onChange,
  setSelection,
  disabled,
  filters,
  setFilters,
  openFiltersPanel,
  plugin,
  canCreate,
  onCreateClick,
  dataViewProps,
  sort,
  setSort,
  isPluginDisabledInEntitlements,
}) => {
  const hasData = useMemo(() => {
    if (dataViewProps?.dataFilter) {
      const filteredResults = dataViewProps.dataFilter(entities);
      return !!filteredResults.length;
    }
    return !!entities.length;
  }, [entities, dataViewProps]);
  const DataView = plugin.CustomDataView || EntityPickerModalListContent;

  return (
    <div
      css={[
        {
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          flex: 1,
          borderRadius: 2,
          border: `1px solid ${theme.colors.charcoal200}`,
          ...(!hasData && {
            alignItems: 'center',
            justifyContent: 'center',
          }),

          ...(disabled && {
            opacity: 0.3,
            pointerEvents: 'none',
          }),
        },
      ]}
    >
      {hasData ? (
        <DataView
          plugin={plugin}
          entities={entities}
          addEntitiesToCache={addEntitiesToCache}
          onLoadMore={onLoadMore}
          disabledIds={disabledIds}
          selectedIds={selectedIds}
          multiple={multiple}
          setSelection={setSelection}
          onChange={onChange}
          loading={loading}
          error={error}
          onTryAgain={onTryAgain}
          filters={filters}
          setFilters={setFilters}
          openFiltersPanel={openFiltersPanel}
          dataViewProps={dataViewProps}
          onUpdate={onUpdate}
          sort={sort}
          setSort={setSort}
          isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
        />
      ) : (
        <EntityPickerModalNoData
          plugin={plugin}
          loading={loading}
          error={error}
          onTryAgain={onTryAgain}
          filters={filters}
          setFilters={setFilters}
          canCreate={canCreate}
          onCreateClick={onCreateClick}
          dataViewProps={dataViewProps}
        />
      )}
    </div>
  );
};

EntityPickerModalContent.propTypes = {
  /**
   * Whether we're loading something from BE.
   */
  loading: PropTypes.bool,
  /**
   * Whether had error fetching data from BE. error is an object when has error thrown by BE
   */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  /**
   * Callback to fetch data again.
   */
  onTryAgain: PropTypes.func,
  /**
   * Load the next page of entities.
   */
  onLoadMore: PropTypes.func,
  /**
   * IDs of selected entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * List of entities.
   */
  entities: PropTypes.arrayOf(entityShape).isRequired,
  /**
   * Callback to handle selection change.
   * Handler is of shape: ({entity, checked}) => *
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func.isRequired,
  /**
   * Whether we're currently in disabled state (saving process, etc.).
   */
  disabled: PropTypes.bool,
  /**
   * The currently applied filters.
   */
  filters: PropTypes.object,
  /**
   * Callback to set filters.
   */
  setFilters: PropTypes.func,
  /**
   * callback to open filters panel
   */
  openFiltersPanel: PropTypes.func,
  /**
   * The plugin to use for selected entity type.
   */
  plugin: pluginShape.isRequired,
  /**
   * A handle method to add entities to cache.
   */
  addEntitiesToCache: PropTypes.func,
  /**
   * Whether the user can create entity of selected type.
   */
  canCreate: PropTypes.bool.isRequired,
  /**
   * Callback when clicking create button
   */
  onCreateClick: PropTypes.func.isRequired,
  /**
   * Additional props to pass to DataView.
   */
  dataViewProps: PropTypes.object,
  /**
   * Callback to handle an entity update.
   * Handler is of shape: (entity) => *
   */
  onUpdate: PropTypes.func,
  /**
   * The currently applied sort options.
   */
  sort: PropTypes.object,
  /**
   * A callback for setting the sort options.
   */
  setSort: PropTypes.func,
  /**
   * Whether the plugin is disabled in entitlements.
   */
  isPluginDisabledInEntitlements: PropTypes.bool,
};

export default EntityPickerModalContent;
